import { useCallback, useEffect, useMemo, useState } from 'react';
import type { OpsOrderDetail } from '../components/details/types';

interface UseFormDirtyTrackingOptions<T> {
  order: OpsOrderDetail;
  initFormState: (order: OpsOrderDetail) => T;
  toSnapshot?: (form: T) => string;
  onReset?: () => void;
}

export function useFormDirtyTracking<T>({
  order,
  initFormState,
  toSnapshot = JSON.stringify,
  onReset,
}: UseFormDirtyTrackingOptions<T>) {
  const [form, setForm] = useState<T>(() => initFormState(order));
  const [initialForm, setInitialForm] = useState<T>(() => initFormState(order));

  useEffect(() => {
    const next = initFormState(order);
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setForm(next);
      setInitialForm(next);
      onReset?.();
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const initialSnapshot = useMemo(
    () => toSnapshot(initialForm),
    [initialForm, toSnapshot],
  );
  const isDirty = useMemo(
    () => toSnapshot(form) !== initialSnapshot,
    [form, initialSnapshot, toSnapshot],
  );

  const resetToInitial = useCallback(() => {
    setForm(initialForm);
    onReset?.();
  }, [initialForm, onReset]);

  return { form, setForm, isDirty, resetToInitial };
}
